import { inspectionDB } from '../../data/database.structure';
import {
  ObservationModel,
  InspectionCategoryTypeModel,
  InspectionModel,
} from '../../data/types';
import {
  ObservationResponse,
  ObservationResponseFromJSON,
} from '../../api-contracts/models/ObservationResponse';
import InspectionProgressMessage from '../../store/loadingInspectionProgressMessage';
import { ApiFactory } from '../api';
import { inspectionRecordService } from './inspectionRecordService';
import { streamJsonArray } from './json-array-stream';

const DEXIE_BATCH_SIZE = 50;
const OBSERVATION_STREAM_BATCH_SIZE = 5000;

interface ObservationMergeTarget {
  inspection: InspectionModel;
  categoryIndex: number;
}

interface ObservationMergeContext {
  observableItemMap: Map<
    number,
    InspectionCategoryTypeModel['observations'][number]['observableItem']
  >;
  categoryTypeTargets: Map<number, ObservationMergeTarget>;
  pendingInspections: Map<string, InspectionModel>;
  /** Each merged Inspection as last read or written, so a record changed since is left alone. */
  readCopies: Map<string, InspectionModel>;
}

export class ObservationStreamService {
  /** Returns the number of observations received. */
  async syncObservations(lastSyncDate: Date | null): Promise<number> {
    InspectionProgressMessage.set({ msg: 'Loading Observations...' });

    const api = ApiFactory.getInspectionApi();
    const response = await api.streamInspectionSyncObservationsRaw({
      lastSyncDate,
    });
    // Context is built lazily on the first batch — after the HTTP round-trip completes,
    // giving any concurrent foreground Dexie writes (e.g. from a simultaneous POST save)
    // more time to settle before we snapshot the inspection table.
    let context: ObservationMergeContext | null = null;
    let savedCount = 0;

    await streamJsonArray<ObservationResponse>(
      response.raw,
      async (observations) => {
        if (!context) {
          context = await this.createMergeContext();
        }
        savedCount += observations.length;
        InspectionProgressMessage.set({
          msg: `Saving ${savedCount.toLocaleString()} observations...`,
        });
        await this.mergeObservationBatch(observations, context);
      },
      {
        batchSize: OBSERVATION_STREAM_BATCH_SIZE,
        mapItem: ObservationResponseFromJSON,
      },
    );

    if (context) {
      await this.flushPendingInspections(context);
    }

    return savedCount;
  }

  private async createMergeContext(): Promise<ObservationMergeContext> {
    const [allInspections, observableItems] = await Promise.all([
      inspectionDB.inspections.toArray(),
      inspectionDB.observableItems.toArray(),
    ]);

    const categoryTypeTargets = new Map<number, ObservationMergeTarget>();
    for (const inspection of allInspections) {
      if (inspection.id <= 0) continue;
      if (!inspection.inspectionCategoryTypes?.length) continue;

      inspection.inspectionCategoryTypes.forEach(
        (categoryType, categoryIndex) => {
          if (categoryType.id > 0) {
            categoryTypeTargets.set(categoryType.id, {
              inspection,
              categoryIndex,
            });
          }
        },
      );
    }

    return {
      observableItemMap: new Map(observableItems.map((oi) => [oi.id, oi])),
      categoryTypeTargets,
      pendingInspections: new Map(),
      readCopies: new Map(),
    };
  }

  private async mergeObservationBatch(
    observations: ObservationResponse[],
    context: ObservationMergeContext,
  ): Promise<void> {
    // Group by categoryTypeId for O(n) lookup
    const obsByCategoryTypeId = new Map<number, ObservationResponse[]>();
    for (const obs of observations) {
      if (obs.categoryTypeId == null) continue;
      const group = obsByCategoryTypeId.get(obs.categoryTypeId);
      if (group) {
        group.push(obs);
      } else {
        obsByCategoryTypeId.set(obs.categoryTypeId, [obs]);
      }
    }

    if (obsByCategoryTypeId.size === 0) return;

    for (const [categoryTypeId, streamedObs] of obsByCategoryTypeId) {
      const target = context.categoryTypeTargets.get(categoryTypeId);
      if (!target) continue;

      const categoryType =
        target.inspection.inspectionCategoryTypes[target.categoryIndex];
      if (!categoryType) continue;

      const existingByExternalId = new Map(
        (categoryType.observations ?? []).map((o) => [o.externalId, o]),
      );

      for (const obs of streamedObs) {
        if (obs.observableItemId == null || !obs.externalId) continue;
        const observableItem = context.observableItemMap.get(
          obs.observableItemId,
        );
        if (!observableItem) {
          console.warn(
            `[ObservationStream] observableItemId=${obs.observableItemId} not in local DB — observation externalId=${obs.externalId} skipped`,
          );
          continue;
        }

        const existing = existingByExternalId.get(obs.externalId);
        existingByExternalId.set(obs.externalId, {
          // Preserve existing local fields first, then overlay server values.
          ...(existing ?? {}),
          id: obs.id ?? 0,
          externalId: obs.externalId,
          categoryTypeId: obs.categoryTypeId ?? categoryType.id,
          dataLevelId: obs.dataLevelId ?? 0,
          entityId: obs.entityId ?? 0,
          observableItemId: obs.observableItemId,
          value: obs.value ?? '',
          unitId: obs.unitId ?? 0,
          actionTypeId: obs.actionTypeId ?? 0,
          entityExternalId: obs.entityExternalId ?? '',
          isSynced: true,
          fileReceived: obs.fileReceived ?? false,
          observableItem,
        } as ObservationModel);
      }

      if (!context.readCopies.has(target.inspection.externalId)) {
        context.readCopies.set(target.inspection.externalId, {
          ...target.inspection,
        });
      }
      const updatedCategoryTypes: InspectionCategoryTypeModel[] = [
        ...target.inspection.inspectionCategoryTypes,
      ];
      updatedCategoryTypes[target.categoryIndex] = {
        ...categoryType,
        observations: Array.from(existingByExternalId.values()),
      };
      target.inspection.inspectionCategoryTypes = updatedCategoryTypes;
      context.pendingInspections.set(target.inspection.externalId, {
        ...target.inspection,
        inspectionCategoryTypes: updatedCategoryTypes,
      });

      if (context.pendingInspections.size >= DEXIE_BATCH_SIZE) {
        await this.flushPendingInspections(context);
      }
    }
  }

  private async flushPendingInspections(
    context: ObservationMergeContext,
  ): Promise<void> {
    if (context.pendingInspections.size === 0) return;

    const written = await inspectionRecordService.applyServerCopies(
      Array.from(context.pendingInspections.values()),
      context.readCopies,
    );
    for (const [externalId, record] of written) {
      context.readCopies.set(externalId, record);
    }
    context.pendingInspections.clear();
  }
}

export const observationStreamService = new ObservationStreamService();
